import dateFormat from "dateformat";
import {
  getMeasurementSerial,
  dataHasEquipmentIdentity,
  normalizeSerial,
} from "./equipmentCompare";

const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const median = (values = []) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

const formatDateTime = (date) =>
  date ? dateFormat(date, "UTC:dd/mm/yy' 'HH:MM") : "--";

/**
 * Qualidade dos dados no período selecionado (client-side).
 * Gaps são estimados pela mediana de intervalo entre medições por equipamento.
 */
export const buildDataQualityReport = ({
  allData = [],
  equipmentOptions = [],
} = {}) => {
  const measurements = Array.isArray(allData) ? allData : [];
  const options = Array.isArray(equipmentOptions) ? equipmentOptions : [];
  const hasIdentity = dataHasEquipmentIdentity(measurements);

  const selectedCount = options.length;
  const bySerial = new Map();

  measurements.forEach((item) => {
    const serial = normalizeSerial(getMeasurementSerial(item));
    if (!serial) return;
    if (!bySerial.has(serial)) bySerial.set(serial, []);
    const date = toDate(item.date);
    if (date) bySerial.get(serial).push(date);
  });

  bySerial.forEach((dates) => {
    dates.sort((a, b) => a.getTime() - b.getTime());
  });

  const withData = [];
  const withoutData = [];
  const intervalsMs = [];
  const gaps = [];

  options.forEach((option) => {
    const serial = normalizeSerial(option.serial);
    const dates = bySerial.get(serial) || [];
    const entry = {
      serial: option.serial,
      label: option.label || option.serial,
      unitName: option.unitName || "",
      count: dates.length,
    };

    if (!dates.length) {
      withoutData.push(entry);
      return;
    }

    withData.push(entry);

    for (let i = 1; i < dates.length; i += 1) {
      const delta = dates[i].getTime() - dates[i - 1].getTime();
      if (delta > 0) intervalsMs.push(delta);
    }
  });

  const medianIntervalMs = median(intervalsMs);
  const gapThresholdMs = Math.max(
    (medianIntervalMs || 0) * 2.5,
    2 * 60 * 60 * 1000
  );

  options.forEach((option) => {
    const serial = normalizeSerial(option.serial);
    const dates = bySerial.get(serial) || [];
    for (let i = 1; i < dates.length; i += 1) {
      const delta = dates[i].getTime() - dates[i - 1].getTime();
      if (delta >= gapThresholdMs) {
        gaps.push({
          serial: option.serial,
          label: option.label || option.serial,
          unitName: option.unitName || "",
          start: dates[i - 1],
          end: dates[i],
          hours: delta / (60 * 60 * 1000),
        });
      }
    }
  });

  gaps.sort((a, b) => b.hours - a.hours);

  let minDate = null;
  let maxDate = null;
  measurements.forEach((item) => {
    const date = toDate(item.date);
    if (!date) return;
    if (!minDate || date < minDate) minDate = date;
    if (!maxDate || date > maxDate) maxDate = date;
  });

  const coveragePercent =
    selectedCount > 0
      ? Math.round((withData.length / selectedCount) * 100)
      : measurements.length > 0
        ? 100
        : 0;

  let status = "empty";
  if (selectedCount === 0 && measurements.length === 0) {
    status = "empty";
  } else if (coveragePercent >= 85 && gaps.length <= 3) {
    status = "good";
  } else if (coveragePercent >= 50) {
    status = "warning";
  } else {
    status = "critical";
  }

  return {
    status,
    hasEquipmentIdentity: hasIdentity,
    selectedCount,
    withDataCount: withData.length,
    withoutDataCount: withoutData.length,
    withoutData: withoutData.slice(0, 12),
    withoutDataTotal: withoutData.length,
    totalMeasurements: measurements.length,
    coveragePercent,
    gapCount: gaps.length,
    gaps: gaps.slice(0, 8).map((gap) => ({
      ...gap,
      startLabel: formatDateTime(gap.start),
      endLabel: formatDateTime(gap.end),
      hoursLabel: gap.hours >= 24
        ? `${(gap.hours / 24).toFixed(1)} d`
        : `${gap.hours.toFixed(1)} h`,
    })),
    medianIntervalMinutes:
      medianIntervalMs != null
        ? Math.max(1, Math.round(medianIntervalMs / (60 * 1000)))
        : null,
    gapThresholdHours: Number((gapThresholdMs / (60 * 60 * 1000)).toFixed(1)),
    dateRange: {
      start: formatDateTime(minDate),
      end: formatDateTime(maxDate),
      startDate: minDate,
      endDate: maxDate,
    },
  };
};
